import * as React from "react";

import {translate} from "react-i18next";

// semantic-ui
import {Table, Input, Dropdown, Icon} from "semantic-ui-react";

// redux
import {ITranslatedComponent} from "../../redux/reducers/generalReducer";

// components
import Button from "../../components/Button";
import Dialog from "../../components/Dialogs/Dialog";
import TextFieldRegEx from "../../components/TextFieldRegEx";
import SelectField from "../../components/SelectField";
import ColorDropdown from "../../components/Table/components/dropdowns/ColorDropdown";
import {getIdentifierInputTypes, IdentifierInputType} from "../../components/Table/dataHandler/Utils";

// services
import {TypefamilyDto, ModelyearDto, ModeltypeBasicDto, PDiPModeltypebasicDto, LanguageDto, ModelBasicNameDto} from "../../services/Client";
import ColumnFactory, {RegexFactory} from "../../services/ColumnFactory";

// helpers
import DataAdapterModeltypeBasic from "./DataAdapterModeltypeBasic";
import {lang2flag} from "../../components/MultiLineColumn";
import {LanguageCodeEnum} from "../AdminView/LanguageTab/LanguageTable";

interface INewModeltypeDialogProps extends ITranslatedComponent {
    /** modelyear for modeltype */
    modelYear: ModelyearDto;

    /** modeltypes to the model year selected */
    modeltypeBasicList: DataAdapterModeltypeBasic[];

    /** typefamilies to the model year selected */
    typefamilies: TypefamilyDto[];

    /** modeltype to be used to prefill the fields */
    templateItem?: DataAdapterModeltypeBasic;

    /** list containing all PDiP modeltype basic data */
    allModeltypeBasicPDiPList: PDiPModeltypebasicDto[];

    /** list containing filtered/unused PDiP modeltype basic data */
    unusedModeltypeBasicPDiPList: PDiPModeltypebasicDto[];

    languageList: LanguageDto[];

    /**
     * called when the user clicks on create of new modeltype
     * @param value new modeltype to be created
     */
    onCreateNewModeltype?(value: DataAdapterModeltypeBasic): void;

    /**
     * called when the user clicks on cancel
     */
    onCancel?(): void;
}

interface INewModeltypeDialogState {
    /** currently creating new item */
    newItem:
    {
        item: DataAdapterModeltypeBasic;

        intSequence: string;

        /** negative fields */
        [key: string]: any;
    };

    inputType: IdentifierInputType;
}

/**
 * shows the dialog to fill the properties for a new modeltype
 */
class NewModeltypeDialog extends React.Component<INewModeltypeDialogProps, INewModeltypeDialogState>
{
    constructor(props: INewModeltypeDialogProps) {
        super(props);

        const {modelYear, languageList} = props;
        const templateItem = !props.templateItem ? undefined : ColumnFactory.deepCopy(props.templateItem.value);

        const names: ModelBasicNameDto[] = languageList.filter((lngDto: LanguageDto) => {
            return [LanguageCodeEnum.BASIC_GERMAN.toString(), LanguageCodeEnum.BASIC_ENGLISH.toString()].indexOf(lngDto.vmce) >= 0;
        }).map((language: LanguageDto) => {
            const newText: ModelBasicNameDto = {
                language: language,
                name: "",
                objectId: 0,
                versionId: 0,
            };
            return newText;
        });

        const dto = Object.assign(
            {},
            templateItem,
            {
                objectId: 0,
                versionId: 0,
                btyp: undefined,
                modelyear: modelYear,
                names: names
            }
        );

        let intSequence = templateItem && templateItem.intSequence ? templateItem.intSequence.toString() : '999';
        let negs: any = {nameNeg: dto.names === undefined, typefamilyNeg: dto.typefamily === undefined}; // TODO Lisa check
        let upItem = new DataAdapterModeltypeBasic(dto as ModeltypeBasicDto, languageList);
        this.state = {
            newItem: {item: upItem, intSequence: intSequence, ...negs},
            inputType: IdentifierInputType.MANUAL_INPUT
        };

        this.handleNewModeltype = this.handleNewModeltype.bind(this);
        this.handleCancel = this.handleCancel.bind(this);
    }

    private typefamilyRenderer = (typefamily: TypefamilyDto) => this.props.t("GLOBAL:" + typefamily.key);

    private handleNewModeltype() {
        const {newItem} = this.state;
        const {languageList} = this.props;

        if (this.props.onCreateNewModeltype) {
            let upItem = new DataAdapterModeltypeBasic(newItem.item.value, languageList);
            upItem.intSequence = newItem.intSequence === '' ? 999 : Number(newItem.intSequence);

            this.props.onCreateNewModeltype(upItem);
        }
    }

    private handleCancel() {
        if (this.props.onCancel) {
            this.props.onCancel();
        }
    }

    private getPDiPBtypRenderKey = (pMTB: PDiPModeltypebasicDto) => {
        return pMTB.btyp + ' (' + pMTB.name + ')';
    }

    renderInputTypeSelection = (): JSX.Element => (
        <Dropdown
            id={'new-derivate-input-type-selection'}
            clearable={false}
            closeOnChange={true}
            selection={true}
            icon={
                <Icon
                    name={this.state.inputType === IdentifierInputType.MANUAL_INPUT ? 'keyboard'
                        : this.state.inputType === IdentifierInputType.ALL_PDIP_DS_INPUT ? 'database' : 'filter'}
                    style={{float: 'left'}}
                />
            }
            options={getIdentifierInputTypes()}
            value={this.state.inputType}
            onChange={(e, v) => {
                const {newItem} = this.state;
                // clean btyp + name + typefamily on switch
                newItem.item.btyp = undefined;
                newItem.item.names[0].name = undefined;
                newItem.item.names[1].name = undefined;
                newItem.item.typefamily = undefined!;
                newItem.typefamilyNeg = true;

                this.setState({
                    inputType: v.value as IdentifierInputType,
                    newItem: newItem
                });

            }}
        />
    )

    renderPDiPDropdown = () => (
        <div style={{flex: 'auto'}}>
            <SelectField
                searchable={true}
                clearable={true}
                autoEscape={true}
                placeholder={this.props.allModeltypeBasicPDiPList.length === 0
                    || (this.state.inputType === IdentifierInputType.UNUSED_PDIP_DS_INPUT && this.props.unusedModeltypeBasicPDiPList.length === 0)
                    ? this.props.t("EMPTY_PDIP_SELECTION") : this.props.t("SELECT_PDIP_BTYP")}
                dataSource={this.state.inputType === IdentifierInputType.ALL_PDIP_DS_INPUT
                    ? this.props.allModeltypeBasicPDiPList : this.props.unusedModeltypeBasicPDiPList}
                value={this.state.newItem.item.btyp}
                valueComparator={(actual: PDiPModeltypebasicDto, expected: string) => actual.btyp === expected}
                rendererKey={this.getPDiPBtypRenderKey}
                filterOption={(pMTB: PDiPModeltypebasicDto, filter: string) =>
                    this.getPDiPBtypRenderKey(pMTB).toUpperCase().includes(filter.toUpperCase())}
                onChange={(value?: PDiPModeltypebasicDto) => {
                    const {newItem} = this.state;

                    newItem.item.btyp = value ? value.btyp : undefined;
                    newItem.item.names.map((name: ModelBasicNameDto) => {
                        name.name = value ? value.name : undefined;
                    });
                    newItem.nameNeg = value === undefined || value.name === undefined || value.name.length === 0;
                    newItem.item.typefamily = value ? this.props.typefamilies.find((tf) => tf.typefamily === value.typefamily)! : undefined!;
                    newItem.typefamilyNeg = value === undefined;

                    this.setState({newItem});
                }}
            />
        </div>
    )

    renderTextControls = (newItem: any): JSX.Element[] => (
        newItem.item.names.map((text: ModelBasicNameDto, index: number) => {
            const flagPrefix = <i className={`${lang2flag((text.language as LanguageDto).vmce)} flag`} />;
            return (
                <React.Fragment key={"texts_" + index}>
                    <Table.Row>
                        <Table.Cell>{flagPrefix} {this.props.t("NAME")}<b>*</b></Table.Cell>
                        <Table.Cell negative={newItem.nameNeg}>
                            <TextFieldRegEx
                                id={'name-txt'+ index}
                                regex={RegexFactory.regex.name}
                                value={text.name}
                                errorText={this.state.newItem.nameNeg ? this.props.t("INPUT_LENGTH_64") : undefined}
                                onChange={(newValue: string, matchGroups: string[] | null, regExError?: string) => {
                                    let upItem = new DataAdapterModeltypeBasic(this.state.newItem.item.value, this.props.languageList);
                                    text.name = newValue;
                                    newItem.item = upItem;
                                    newItem.nameNeg = matchGroups === null; // reg ex error
                                    this.setState({newItem});
                                }}
                            />
                        </Table.Cell>
                    </Table.Row>
                </React.Fragment>
            );
        })
    )

    render() {
        const {t} = this.props;
        const columnRegEx = RegexFactory.regex;
        const {typefamilies, modeltypeBasicList, languageList} = this.props;
        const {newItem} = this.state;

        // not filled mandatory fields should be true
        let {btypNeg = false, nameNeg = true, typefamilyNeg = true} = newItem;

        let errorTextBtyp = undefined;
        if (btypNeg) {
            errorTextBtyp = t("INPUT_LENGTH_6");
        }
        else if (newItem.item.btyp && newItem.item.btyp.length === 6) {
            const btypIndex = modeltypeBasicList.findIndex((row) => row.btyp === newItem.item.btyp);
            if (btypIndex >= 0) {
                // the same btyp is already used
                btypNeg = true;
                errorTextBtyp = t("ERRORS:MODELTYPE_BASIC_EXISTS", {btyp: newItem.item.btyp});
            }
        }

        let actions =
            [
                <Button key={1} id='submit-derivate' icon={"checkmark"} disabled={btypNeg || nameNeg || typefamilyNeg} onClick={this.handleNewModeltype} />,
                <Button key={2} icon={"ban"} onClick={this.handleCancel} />,
            ];
        return (
            <Dialog
                title={t("NEW_MODELTYPE")}
                open={true}
                actions={actions}
                fullWidth={false}
            >
                <Table>
                    <Table.Body>
                        <Table.Row>
                            <Table.Cell width={3}>{t("COLUMN_FACTORY:BTYP")}</Table.Cell>
                            <Table.Cell negative={btypNeg} className={'display--flex align-items--center'}>
                                {this.state.inputType === IdentifierInputType.MANUAL_INPUT ?
                                    <TextFieldRegEx
                                        id={'btyp-txt'}
                                        regex={columnRegEx.btyp}
                                        value={newItem.item.btyp}
                                        errorText={errorTextBtyp}
                                        onChange={(newValue: string, matchGroups: string[] | null, regExError?: string) => {
                                            let upItem = new DataAdapterModeltypeBasic(newItem.item.value, languageList);
                                            upItem.btyp = newValue;
                                            newItem.item = upItem;
                                            newItem.btypNeg = matchGroups === null; // reg ex error
                                            this.setState({newItem});
                                        }}
                                    />
                                    :
                                    this.renderPDiPDropdown()
                                }
                                {this.renderInputTypeSelection()}
                            </Table.Cell>
                        </Table.Row>
                        {this.renderTextControls(newItem)}
                        <Table.Row>
                            <Table.Cell>{t("TYPEFAMILY")}<b>*</b></Table.Cell>
                            <Table.Cell negative={typefamilyNeg} id="typefamily">
                                <SelectField
                                    clearable={false}
                                    dataSource={typefamilies}
                                    value={newItem.item.typefamily}
                                    rendererKey={this.typefamilyRenderer}
                                    disabled={this.state.inputType !== IdentifierInputType.MANUAL_INPUT}
                                    onChange={(selectedValue?: TypefamilyDto) => {
                                        let upItem = new DataAdapterModeltypeBasic(newItem.item.value, languageList);
                                        upItem.typefamily = selectedValue!;
                                        newItem.item = upItem;
                                        newItem.typefamilyNeg = !selectedValue; // nothing selected
                                        this.setState({newItem});
                                    }}
                                />
                            </Table.Cell>
                        </Table.Row>
                        <Table.Row>
                            <Table.Cell>{t("MT_BASIC_VIEW:INT_SEQUENCE")}</Table.Cell>
                            <Table.Cell>
                                <Input
                                    fluid={true}
                                    placeholder={t("DEFAULT_SEQUENCE_VALUE")}
                                    value={newItem.intSequence}
                                    onChange={(e) => {
                                        // '' is also allowed
                                        let newValue = Number(e.currentTarget.value.trim());
                                        if (!isNaN(newValue) && Number.isInteger(newValue)) {
                                            newItem.intSequence = e.currentTarget.value.trim() === '' ? '' : newValue.toString();

                                            this.setState({newItem});
                                        }
                                    }}
                                />
                            </Table.Cell>
                        </Table.Row>
                        <Table.Row>
                            <Table.Cell>{t("MT_BASIC_VIEW:COLOR")}</Table.Cell>
                            <Table.Cell>
                                <ColorDropdown
                                    value={newItem.item.color}
                                    onChange={(value: string | undefined) => {
                                        newItem.item.color = value;
                                        this.setState({newItem});
                                    }}
                                    isDarkMode={true}
                                    readOnly={false}
                                />
                            </Table.Cell>
                        </Table.Row>
                    </Table.Body>
                </Table>
                <div><b>* {this.props.t("MANDATORY_FIELD")}</b></div>
            </Dialog>
        );
    }
}

export default translate(["MT_BASIC_VIEW", "COLUMN_FACTORY"])(NewModeltypeDialog);



// WEBPACK FOOTER //
// ./src/views/ModeltypeBasicView/NewModeltypeDialog.tsx